<template>
  <AppCard class="mt-6 animate-fadein animate-duration-1000">
    <div class="flex w-fit mb-4">
      <button
        type="button"
        :class="[
          'py-[0.55rem] px-4 border focus:outline-none transition-colors duration-200 rounded-l-md text-sm font-medium',
          filter === 'paid'
            ? 'bg-blue-500 text-white border-blue-500 z-10'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
        ]"
        @click="setFilter('paid')"
      >
        History
      </button>
      <button
        type="button"
        :class="[
          'py-[0.55rem] px-4 border focus:outline-none transition-colors duration-200 -ml-px rounded-r-md text-sm font-medium',
          filter === 'scheduled'
            ? 'bg-blue-500 text-white border-blue-500 z-10'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
        ]"
        @click="setFilter('scheduled')"
      >
        Scheduled
      </button>
    </div>

    <div class="flex flex-col sm:flex-row gap-3 mb-4">
      <IconField class="w-full sm:w-80">
        <InputIcon>
          <i class="pi pi-search" />
        </InputIcon>
        <InputText
          v-model="searchQuery"
          placeholder="Search by contact or player name"
          class="w-full"
          @input="handleSearchChange"
        />
      </IconField>

      <Select
        v-model="typeFilter"
        :options="OFFERING_TYPE_OPTIONS"
        option-label="label"
        option-value="value"
        placeholder="Filter by Type"
        class="w-full sm:w-56"
        show-clear
        @change="handleFilterChange"
      />

      <Select
        v-if="filter === 'paid'"
        v-model="statusFilter"
        :options="PAYMENT_STATUS_OPTIONS"
        option-label="label"
        option-value="value"
        placeholder="Filter by Status"
        class="w-full sm:w-56"
        show-clear
        @change="handleFilterChange"
      />

      <Button
        icon="pi pi-download"
        text
        rounded
        class="sm:ml-auto"
        :loading="isExporting"
        aria-label="Export payments log"
        @click="handleExport"
      />
    </div>

    <Tabs value="0">
      <TabPanel value="0" header="Payments Log">
        <div class="relative">
          <div
            v-if="isLoading"
            class="absolute inset-0 bg-white/50 z-10 flex items-center justify-center"
          >
            <ProgressSpinner />
          </div>

          <DataTable
            :value="payments"
            striped-rows
            row-hover
            selection-mode="single"
            :sort-field="currentSortField"
            :sort-order="currentSortOrder"
            @row-click="onRowClick"
            @sort="handleSort"
          >
            <Column
              v-if="filter === 'paid'"
              header="Processed Date"
              field="payment_date"
              sortable
            >
              <template #body="slotProps">
                <div v-if="slotProps.data.processed_at" class="flex flex-col">
                  <span class="text-base">
                    {{
                      getFormattedDate(
                        slotProps.data.processed_at,
                        DateFormat.DEFAULT,
                        currentOrganizationTimezone
                      )
                    }}
                  </span>
                  <span class="text-sm text-gray-500">
                    {{
                      getFormattedTime(
                        slotProps.data.processed_at,
                        currentOrganizationTimezone
                      )
                    }}
                    <span data-testid="processed-date-timezone">
                      {{
                        getFormattedTimezone(
                          slotProps.data.processed_at,
                          currentOrganizationTimezone
                        )
                      }}
                    </span>
                  </span>
                </div>
                <div v-else class="text-base">-</div>
              </template>
            </Column>
            <Column header="Due Date" field="scheduled_at" sortable>
              <template #body="slotProps">
                <div class="text-base">
                  {{ getFormattedDate(slotProps.data.scheduled_at) }}
                  <span class="text-gray-500" data-testid="due-date-timezone">
                    {{
                      getFormattedTimezone(
                        slotProps.data.scheduled_at,
                        currentOrganizationTimezone
                      )
                    }}
                  </span>
                </div>
              </template>
            </Column>
            <Column header="Paid by">
              <template #body="slotProps">
                <div class="text-base">
                  {{ slotProps.data.paid_by }}
                </div>
              </template>
            </Column>
            <Column header="Type">
              <template #body="slotProps">
                <div class="text-base">
                  {{ formatType(slotProps.data.type) }}
                </div>
              </template>
            </Column>
            <Column header="Source">
              <template #body="slotProps">
                <div class="text-base truncate max-w-[16ch]">
                  <span
                    v-if="getSourceRoute(slotProps.data)"
                    class="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                    @click.stop="navigateToSource(slotProps.data)"
                  >
                    {{ slotProps.data.source_name || "N/A" }}
                  </span>
                  <span v-else>
                    {{ slotProps.data.source_name || "N/A" }}
                  </span>
                </div>
              </template>
            </Column>
            <Column header="Method">
              <template #body="slotProps">
                <div class="text-base">
                  {{ formatPaymentMethod(slotProps.data.payment_method) }}
                </div>
              </template>
            </Column>
            <Column header="Amount" field="amount" sortable>
              <template #body="slotProps">
                <div class="text-base">
                  <AmendedAmountCell
                    :amount="slotProps.data.amount"
                    :is-amended="slotProps.data.is_amended"
                    :original-amount="slotProps.data.original_amount"
                  />
                </div>
              </template>
            </Column>
            <Column header="Fee" field="fee_amount">
              <template #body="slotProps">
                <div class="text-base text-gray-500">
                  {{ getFormattedCurrency(slotProps.data.fee_amount) }}
                </div>
              </template>
            </Column>
            <Column header="Status">
              <template #body="slotProps">
                <div class="text-base">
                  <i
                    :class="`pi pi-circle-fill text-xs ${getPaymentStatusColor(slotProps.data.status)}`"
                  ></i>
                  {{ formatPaymentStatus(slotProps.data.status) }}
                </div>
              </template>
            </Column>
            <Column header="">
              <template #body="slotProps">
                <PaymentActionsMenu
                  :payment="slotProps.data"
                  :subscriber-membership-id="
                    getSubscriberMembershipId(slotProps.data)
                  "
                  @action-success="handleActionSuccess"
                  @refund="confirmRefund"
                />
              </template>
            </Column>
          </DataTable>

          <Paginator
            :always-show="true"
            :rows="pageSize"
            :total-records="totalRecords"
            template="
               FirstPageLink
               PrevPageLink
               CurrentPageReport
               NextPageLink
               LastPageLink
               RowsPerPageDropdown"
            current-page-report-template="Showing {first} to {last} of {totalRecords}"
            @page="onPageChange"
          />
        </div>
      </TabPanel>
    </Tabs>

    <PaymentDetailDrawer
      v-model:visible="drawerVisible"
      :payment-id="selectedPaymentId"
      booking-source-opens-drawer
      @action-success="handleActionSuccess"
      @refund="confirmRefund"
      @open-booking-detail="openBookingDetail"
    />

    <BookingDetailDrawer
      ref="bookingDetailDrawer"
      v-model:visible="bookingDrawerVisible"
      :booked-service-id="selectedBookedServiceId"
      @changed="onBookingChanged"
      @add-participant="onAddParticipant"
    />

    <CreateBookingDrawer
      v-model:visible="isCreateBookingDrawerVisible"
      :preset="createBookingPreset"
      @created="onBookingCreated"
    />

    <ConfirmDialog group="refund">
      <template #container="{ message, rejectCallback }">
        <div class="flex flex-col p-8 bg-surface-0 dark:bg-surface-900 rounded">
          <h3 class="text-xl font-bold mb-4">{{ message.header }}</h3>
          <p class="whitespace-pre-line mb-6">{{ message.message }}</p>
          <div class="flex justify-end gap-6">
            <button
              type="button"
              class="text-gray-600 hover:text-gray-800"
              :disabled="isRefunding"
              @click="rejectCallback"
            >
              Cancel
            </button>
            <Button :loading="isRefunding" @click="handleRefund">
              Yes, refund
            </Button>
          </div>
        </div>
      </template>
    </ConfirmDialog>
  </AppCard>
</template>

<script setup lang="ts">
import DataTable, {
  type DataTableRowClickEvent,
  type DataTableSortEvent,
} from "primevue/datatable";
import Column from "primevue/column";
import TabPanel from "primevue/tabpanel";
import Tabs from "primevue/tabs";
import Paginator, { type PageState } from "primevue/paginator";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import ProgressSpinner from "primevue/progressspinner";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import ConfirmDialog from "primevue/confirmdialog";

import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useRouterNavigation } from "@/composables/useRouterNavigation";

import AppCard from "@/shared/components/AppCard.vue";
import PaymentDetailDrawer from "./PaymentDetailDrawer.vue";
import PaymentActionsMenu from "./PaymentActionsMenu.vue";
import AmendedAmountCell from "./AmendedAmountCell.vue";
import {
  defaultPaymentsOrderingFor,
  getBookingSourceId,
  getPaymentSourceRoute,
  getSubscriberMembershipId,
} from "@/modules/payments/helpers";
import type { CreateBookingPreset } from "@/modules/bookings/types";

// Lazy-loaded so the schedule/bookings drawer code stays out of the payments
// bundle until an admin actually opens a booking from a payment's Source.
const BookingDetailDrawer = defineAsyncComponent(
  () => import("@/modules/schedule/components/BookingDetailDrawer.vue")
);
const CreateBookingDrawer = defineAsyncComponent(
  () => import("@/modules/bookings/components/CreateBookingDrawer.vue")
);

import { ROUTE_NAME } from "@/shared/constants/routes";

import { usePaymentsLog } from "@/modules/payments/composables/usePaymentsLog";
import { useRefundScheduledPayment } from "@/modules/payments/composables/useRefundScheduledPayment";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import {
  DateFormat,
  downloadBlob,
  toDateString,
  getFormattedCurrency,
  getFormattedDate,
  getFormattedTime,
  getFormattedTimezone,
} from "@/shared/utils/helpers";
import { getPaymentsLogCsv } from "@/modules/payments/api";
import {
  formatPaymentMethod,
  formatPaymentStatus,
  getPaymentStatusColor,
} from "@/modules/payments/helpers";
import {
  OFFERING_TYPE_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
} from "@/modules/payments/constants";

import type { PaymentsLog } from "@/api/model";

const route = useRoute();
const router = useRouter();

const { currentOrganizationTimezone } = useCurrentOrganization();

const {
  paginatedPayments,
  fetchPayments,
  buildFilterParams,
  currentPage,
  pageSize,
  totalRecords,
  filter,
  paymentFetchError,
  isLoading,
  searchQuery,
  typeFilter,
  statusFilter,
  sortField,
} = usePaymentsLog();

const emit = defineEmits<{
  "action-success": [];
}>();

const payments = computed(() => paginatedPayments.value?.results);
const toast = useToast();
const confirm = useConfirm();
const {
  refundScheduledPayment,
  errorMessage: refundError,
  isLoading: isRefunding,
} = useRefundScheduledPayment();
const pendingRefundPayment = ref<PaymentsLog | null>(null);

const isExporting = ref(false);

/**
 * Mirrors the three cases the backend names the file after: the log's one
 * toggle plus the export date. type/status/search are deliberately left out —
 * the toggle is what makes the file mean something different (an AR list vs. a
 * payment history).
 *
 * This name is authoritative for the in-app path: downloadBlob sets
 * link.download, which wins over the response's Content-Disposition. The
 * backend's _build_filename (payments_log_csv_renderer.py) is the fallback for
 * direct-URL hits. Both derive from `filter`, so they can disagree only on the
 * date, across a UTC/org-local midnight boundary. If that ever matters, read
 * the header instead of duplicating this.
 */
function buildExportFilename(): string {
  const exportedOn = toDateString(new Date());
  if (filter.value === "scheduled")
    return `payments-log-scheduled-${exportedOn}.csv`;
  if (filter.value === "paid") return `payments-log-history-${exportedOn}.csv`;
  return `payments-log-all-${exportedOn}.csv`;
}

/**
 * The backend's message, not axios'.
 *
 * Because the request sets responseType: "blob", axios hands the error body
 * back as a Blob, so String(err) is only ever "Request failed with status code
 * 400" — swallowing the two messages the admin needs to act on: the row cap
 * ("narrow your filters") and an invalid ordering param.
 */
async function extractExportError(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.error === "string") return parsed.error;
    } catch {
      // Body wasn't JSON (a proxy error page, say) — use the generic message.
    }
  }
  return String(err);
}

async function handleExport() {
  if (isExporting.value) return;
  isExporting.value = true;
  try {
    // Same params as the table fetch, minus pagination: the export is the
    // rows the admin is looking at, not just the page.
    const blob = await getPaymentsLogCsv(buildFilterParams());
    downloadBlob(blob, buildExportFilename());
  } catch (err) {
    toast.add({
      severity: "error",
      summary: "Export failed",
      detail: await extractExportError(err),
      life: 4000,
    });
  } finally {
    isExporting.value = false;
  }
}

const drawerVisible = ref(false);
const selectedPaymentId = ref<string | null>(null);

// --- Booking detail drawer (opened from a booking-session Source) ---
const bookingDrawerVisible = ref(false);
const selectedBookedServiceId = ref<string | null>(null);
const bookingDetailDrawer = ref<{ refresh: () => void } | null>(null);

// --- Create booking drawer (hosts the booking drawer's Add Participant flow) ---
const isCreateBookingDrawerVisible = ref(false);
const createBookingPreset = ref<CreateBookingPreset | null>(null);

const openBookingDetail = (bookedServiceId: string) => {
  // Avoid stacking two right-side drawers: close the payment drawer first.
  drawerVisible.value = false;
  selectedBookedServiceId.value = bookedServiceId;
  bookingDrawerVisible.value = true;
};

const onBookingChanged = () => {
  // A booking action (cancel/refund/etc.) can change payment rows.
  fetchPayments();
};

const onAddParticipant = (preset: CreateBookingPreset) => {
  createBookingPreset.value = preset;
  isCreateBookingDrawerVisible.value = true;
};

const onBookingCreated = () => {
  fetchPayments();
  if (bookingDrawerVisible.value) {
    bookingDetailDrawer.value?.refresh();
  }
};

watch(isCreateBookingDrawerVisible, (open) => {
  if (!open) {
    createBookingPreset.value = null;
  }
});

const currentSortField = computed(() => {
  const field = sortField.value.replace(/^-/, "");
  return field;
});

const currentSortOrder = computed(() => {
  return sortField.value.startsWith("-") ? -1 : 1;
});

if (paymentFetchError.value) {
  toast.add({
    severity: "error",
    summary: "Error",
    detail: paymentFetchError.value,
  });
}

const setFilter = (value: "paid" | "scheduled") => {
  filter.value = value;
  currentPage.value = 0;
  sortField.value = defaultPaymentsOrderingFor(value);
  fetchPayments();
};

const handleSearchChange = () => {
  currentPage.value = 0;
  fetchPayments();
};

const handleFilterChange = () => {
  currentPage.value = 0;
  fetchPayments();
};

const handleSort = (event: DataTableSortEvent) => {
  const field = event.sortField as string;
  const order = event.sortOrder === 1 ? "" : "-";
  sortField.value = `${order}${field}`;
  fetchPayments();
};

const onPageChange = (event: PageState) => {
  currentPage.value = event.page;
  fetchPayments();
};

const onRowClick = (event: DataTableRowClickEvent) => {
  selectedPaymentId.value = event.data.id;
  drawerVisible.value = true;
  router.push({
    name: ROUTE_NAME.PAYMENT_LOG,
    query: { ...route.query, paymentId: event.data.id },
  });
};

const handleActionSuccess = () => {
  fetchPayments();
  emit("action-success");
};

const handleRefund = async () => {
  const payment = pendingRefundPayment.value;
  if (!payment) return;

  const subscriberMembershipId = getSubscriberMembershipId(payment);
  if (!subscriberMembershipId) {
    pendingRefundPayment.value = null;
    confirm.close();
    return;
  }

  await refundScheduledPayment(subscriberMembershipId, payment.id);

  pendingRefundPayment.value = null;
  confirm.close();

  if (refundError.value) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Our team has been notified and we are looking into it",
      life: 3000,
    });
    return;
  }

  toast.add({
    severity: "success",
    summary: "Success",
    detail: "Payment has been refunded successfully",
    life: 3000,
  });

  fetchPayments();
  emit("action-success");
};

const confirmRefund = (payment: PaymentsLog) => {
  if (!getSubscriberMembershipId(payment)) return;

  pendingRefundPayment.value = payment;
  confirm.require({
    group: "refund",
    header: "Confirm Refund",
    message: `Are you sure you want to refund this ${getFormattedCurrency(payment.amount)} payment${payment.paid_by ? ` to ${payment.paid_by}` : ""}?`,
  });
};

watch(
  () => route.query.paymentId,
  (paymentId) => {
    if (paymentId && typeof paymentId === "string") {
      selectedPaymentId.value = paymentId;
      drawerVisible.value = true;
    } else if (!paymentId && drawerVisible.value) {
      drawerVisible.value = false;
      selectedPaymentId.value = null;
    }
  },
  { immediate: true }
);

watch(drawerVisible, (newVisible) => {
  if (!newVisible && route.query.paymentId) {
    const query = { ...route.query };
    delete query.paymentId;
    router.push({
      name: ROUTE_NAME.PAYMENT_LOG,
      query,
    });
  }
});

const formatType = (type: string) => {
  if (!type) {
    return "N/A";
  }
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

const getSourceRoute = (payment: PaymentsLog) => {
  return getPaymentSourceRoute(payment);
};

const { navigateWithQuery } = useRouterNavigation();

const navigateToSource = (payment: PaymentsLog) => {
  const bookingSourceId = getBookingSourceId(payment);
  if (bookingSourceId) {
    openBookingDetail(bookingSourceId);
    return;
  }
  const route = getSourceRoute(payment);
  if (route) {
    navigateWithQuery(route);
  }
};

onMounted(() => {
  fetchPayments();
});
</script>

<style scoped>
:deep(.p-datatable .p-datatable-tbody > tr) {
  cursor: pointer;
}
</style>
